/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2026.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import {QFieldMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QFieldMetaData";
import {useFormikContext} from "formik";
import {useMemo} from "react";
import {flushSync} from "react-dom";
import DynamicFormUtils from "qqq/components/forms/DynamicFormUtils";


/***************************************************************************
 * Hook that wires an onChange handler implementing the TO_UPPER_CASE /
 * TO_LOWER_CASE field behaviors used by both DynamicFormField (RecordScreen)
 * and SimpleInputEditCell (row-edit grid).  Transforms keep the cursor
 * position stable across the re-render via flushSync + setSelectionRange
 * (see https://giacomocerquone.com/blog/keep-input-cursor-still).
 *
 * If neither case behavior is active and no onChangeCallback is provided,
 * onChange is undefined so callers can let Formik's built-in Field handle
 * change events normally.
 *
 * Element lookup uses `input[name="..."]` querySelector — works for both
 * Formik <Field as={OutlinedInput}> (row-edit) and MDInput-style wrappers
 * (RecordScreen), since both render the underlying <input name="…"> element.
 ***************************************************************************/
export interface UseCaseTransformOnChangeResult
{
   onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
   hasCaseTransform: boolean;
}


export function useCaseTransformOnChange(fieldName: string, fieldMetaData: QFieldMetaData | undefined, onChangeCallback?: (newValue: any) => void): UseCaseTransformOnChangeResult
{
   const {setFieldValue} = useFormikContext<any>();
   const isToUpperCase = useMemo(() => DynamicFormUtils.isToUpperCase(fieldMetaData), [fieldMetaData]);
   const isToLowerCase = useMemo(() => DynamicFormUtils.isToLowerCase(fieldMetaData), [fieldMetaData]);
   const hasCaseTransform = isToUpperCase || isToLowerCase;

   if (!hasCaseTransform && !onChangeCallback)
   {
      return {hasCaseTransform: false};
   }

   const onChange = (e: React.ChangeEvent<HTMLInputElement>) =>
   {
      if (hasCaseTransform)
      {
         const beforeStart = e.target.selectionStart;
         const beforeEnd = e.target.selectionEnd;
         flushSync(() =>
         {
            let newValue = e.currentTarget.value;
            if (isToUpperCase) newValue = newValue.toUpperCase();
            if (isToLowerCase) newValue = newValue.toLowerCase();
            setFieldValue(fieldName, newValue);
            if (onChangeCallback)
            {
               onChangeCallback(newValue);
            }
         });
         const input = document.querySelector(`input[name='${fieldName}']`) as HTMLInputElement | null;
         if (input)
         {
            input.setSelectionRange(beforeStart, beforeEnd);
         }
      }
      else if (onChangeCallback)
      {
         onChangeCallback(e.currentTarget.value);
      }
   };

   return {onChange, hasCaseTransform};
}
